import React from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { useStream } from '../../../utils/hooks/useStream';
import { StreamType } from '../../../Stream/types';
import { createCallbackActionCreator } from '../../../Stream/helpers';
import ChooseAvatarModal from './ChooseAvatarModal';
import ExclusiveAvatarGreetingsModal from '../ExclusiveAvatarGreetingsModal';

const CHOOSE_AVATAR_DEVIATION_STREAM_ID = 'choose-avatar-deviations';

// Create callback action creator for the stream
const fetchNextChooseAvatarDeviations = createCallbackActionCreator(
  'FETCH_NEXT_CHOOSE_AVATAR_DEVIATIONS'
);

export interface Props {
  /** Callback when modal is closed */
  onClose?: () => void;
  /** Callback when a deviation is selected and applied */
  onSelect?: (selectedDeviation: PapiDeviation | null) => void;
  /** Currently selected/active deviation being used as avatar */
  currentlySelectedDeviation?: PapiDeviation | null;
}

/**
 * Wrapper component that fetches deviations and conditionally renders:
 * - ExclusiveAvatarGreetingsModal if no deviations are available
 * - ChooseAvatarModal if deviations are available
 */
const ChooseAvatarModalWrapper: React.FC<Props> = ({
  onClose,
  onSelect,
  currentlySelectedDeviation,
}) => {
  // Stream for avatar deviations
  const streamId = CHOOSE_AVATAR_DEVIATION_STREAM_ID;
  const streamData = useStream<PapiDeviation>(streamId, 500, {
    streamType: StreamType.WITH_OFFSET,
    autoFetch: true,
    itemsPerFetch: 24,
    items: [],
    hasMore: true,
    fetchNextCallback: fetchNextChooseAvatarDeviations,
  });

  const {
    items: deviations,
    isInitialized,
    isFetching,
    hasMore,
    fetchNext,
    errorMessage,
  } = streamData;

  // If initialized and no deviations, show greetings modal
  if (deviations.length === 0) {
    if (isFetching) {
      return <LoadingIndicator usePortal withOverlay />;
    }
    return <ExclusiveAvatarGreetingsModal onClose={onClose} />;
  }

  // Otherwise show the regular choose avatar modal
  return (
    <ChooseAvatarModal
      onClose={onClose}
      onSelect={onSelect}
      onLoadMore={() => {
        if (!isFetching && hasMore && !errorMessage) {
          fetchNext();
        }
      }}
      deviations={deviations}
      isInitialized={isInitialized}
      currentlySelectedDeviation={currentlySelectedDeviation}
    />
  );
};

ChooseAvatarModalWrapper.displayName = 'ChooseAvatarModalWrapper';

export default ChooseAvatarModalWrapper;
